<!-- TelcoTrack - Construction Diary Entry Modal -->
<!-- Zápis stavebního deníku: počasí, pracovníci, práce, odchylky, BOZP, poznámky. -->
<app-modal
  [isOpen]="isOpen"
  title="Stavební deník"
  [showFooter]="true"
  width="min(92vw, 80rem)"
  (closeEvent)="onClose()">

  <!-- Hlavička s metadaty -->
  <section *ngIf="entry" class="tt-detail-section mb-6" aria-labelledby="diary-meta-heading">
    <h2 id="diary-meta-heading" class="tt-detail-section__title">Záznam</h2>
    <dl class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div class="detail-item">
        <dt class="tt-detail-label">Datum</dt>
        <dd class="tt-detail-value">{{ formatDate(entry.entryDate) }}</dd>
      </div>
      <div class="detail-item">
        <dt class="tt-detail-label">Vytvořil</dt>
        <dd class="tt-detail-value">{{ entry.createdBy }}</dd>
      </div>
      <div class="detail-item" *ngIf="entry.confirmedBy">
        <dt class="tt-detail-label">Potvrdil</dt>
        <dd class="tt-detail-value">{{ entry.confirmedBy }} ({{ formatDateTime(entry.confirmedAt) }})</dd>
      </div>
      <div class="detail-item">
        <dt class="tt-detail-label">Stav</dt>
        <dd class="tt-detail-value">
          <span *ngIf="isLocked" class="text-success-700 dark:text-success-400 font-medium">Potvrzeno</span>
          <span *ngIf="!isLocked && canEditEntry && entry?.lifecycleStatus === 'not_started'" class="font-medium">Nezačato</span>
          <span *ngIf="!isLocked && canEditEntry && (entry?.lifecycleStatus === 'in_progress' || !entry?.lifecycleStatus)" class="text-warning-700 dark:text-warning-400 font-medium">Rozpracováno</span>
          <span *ngIf="!isLocked && !canEditEntry" class="font-medium">Jen prohlížení</span>
        </dd>
      </div>
    </dl>
    <p *ngIf="!isLocked && canEditEntry && entry?.lifecycleStatus === 'not_started'" class="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
      Zatím nebylo uloženo žádné ruční doplnění — zobrazená data jsou z automatické přípravy a z polygonu.
    </p>
    <p *ngIf="!isLocked && canEditEntry" class="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
      Záznam lze uzavřít podpisem do {{ editWindowDays }} dnů od data zápisu. Po potvrzení je neměnný.
    </p>
    <div *ngIf="entry.drivePdfUrl" class="mt-2">
      <a [href]="entry.drivePdfUrl" target="_blank" rel="noopener"
         class="text-sm text-primary-600 dark:text-primary-400 hover:underline">Otevřít PDF (Google Drive)</a>
    </div>
    <p *ngIf="isLocked" class="mt-4 mb-0 text-sm text-neutral-600 dark:text-neutral-300">
      Platná verze je uzamčená. Další uložení vytvoří pouze dodatek; uzamčený záznam ani PDF se tím nepřepíše.
    </p>
  </section>

  <app-skeleton *ngIf="detailLoading" variant="detail" [columns]="4" label="Načítání detailu deníku…"></app-skeleton>

  <form *ngIf="!detailLoading" class="tt-panel-enter" [formGroup]="form" (ngSubmit)="onSave()">
    <div class="space-y-6">

      <!-- Identifikace stavby -->
      <section class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Identifikace stavby</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="detail-item">
            <label for="diary-construction-name" class="tt-detail-label">Název stavby</label>
            <input id="diary-construction-name" formControlName="constructionName" class="input">
          </div>
          <div class="detail-item">
            <label for="diary-stavebnik" class="tt-detail-label">Stavebník / investor</label>
            <input id="diary-stavebnik" formControlName="stavebnik" class="input">
          </div>
          <div class="detail-item">
            <label for="diary-projektant" class="tt-detail-label">Projektant</label>
            <input id="diary-projektant" formControlName="projektant" class="input">
          </div>
          <div class="detail-item">
            <label for="diary-zhotovitel" class="tt-detail-label">Zhotovitel</label>
            <input id="diary-zhotovitel" formControlName="zhotovitel" class="input">
          </div>
          <div class="detail-item">
            <label for="diary-stavebni-povoleni" class="tt-detail-label">Č. stavebního povolení</label>
            <input id="diary-stavebni-povoleni" formControlName="stavebniPovoleniCislo" class="input">
          </div>
          <div class="detail-item">
            <label for="diary-stavbyvedouci" class="tt-detail-label">Vedoucí stavby</label>
            <input id="diary-stavbyvedouci" formControlName="stavbyveduciName" class="input">
          </div>
          <div class="detail-item">
            <label for="diary-obec" class="tt-detail-label">Obec</label>
            <input id="diary-obec" formControlName="obec" class="input" placeholder="Název obce">
          </div>
          <div class="detail-item">
            <label for="diary-registracni-cislo" class="tt-detail-label">Registrační číslo projektu</label>
            <input id="diary-registracni-cislo" formControlName="registracniCisloProjektu" class="input" placeholder="např. CZ01.xxxxx">
          </div>
        </div>
      </section>

      <!-- Počasí — automaticky předvyplněno z Open-Meteo při otevření záznamu. -->
      <section class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Počasí</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="detail-item">
            <label for="diary-weather-temp-morning" class="tt-detail-label">Teplota ráno (°C)</label>
            <input id="diary-weather-temp-morning"
                   type="number"
                   formControlName="weatherTempMorningC"
                   class="input">
          </div>
          <div class="detail-item">
            <label for="diary-weather-temp-afternoon" class="tt-detail-label">Teplota odpoledne (°C)</label>
            <input id="diary-weather-temp-afternoon"
                   type="number"
                   formControlName="weatherTempAfternoonC"
                   class="input">
          </div>
          <div class="detail-item">
            <label for="diary-weather-condition" class="tt-detail-label">Podmínky</label>
            <select id="diary-weather-condition" formControlName="weatherCondition"
                    class="select">
              <option *ngFor="let o of weatherOptions" [ngValue]="o.value">{{ o.label }}</option>
            </select>
          </div>
        </div>
        <p class="text-xs text-neutral-500 mt-2">
          Teplota a podmínky jsou předvyplněny z Open-Meteo pro lokaci polygonu. Můžete je ručně upravit.
        </p>
        <div class="detail-item">
          <label for="diary-weather-note" class="tt-detail-label">Poznámka k počasí</label>
          <textarea id="diary-weather-note" formControlName="weatherNote" rows="2"
                    class="textarea"></textarea>
        </div>
      </section>

      <!-- Pracovníci ze specifikace — v tento den je lze odebrat; další den se šablona kopíruje znovu -->
      <section *ngIf="entry" class="tt-detail-section space-y-4">
        <ng-container *ngIf="autoWorkersList.length; else noAutoWorkers">
          <button *ngIf="!canEditEntry && autoWorkersCollapsible"
                  type="button"
                  class="diary-work-summary__header"
                  [attr.aria-expanded]="autoWorkersExpanded"
                  (click)="toggleAutoWorkersExpanded()">
            <div class="min-w-0 flex-1 text-left">
              <h2 class="tt-detail-section__title mb-0">
                Pracovníci na stavbě ({{ autoWorkersList.length }})
              </h2>
              <p class="diary-work-summary__toggle-hint m-0 mt-0.5">
                {{ autoWorkersExpanded ? 'Skrýt' : ('Zobrazit všech ' + autoWorkersList.length) }}
              </p>
            </div>
            <svg class="diary-work-summary__chevron"
                 [class.diary-work-summary__chevron--expanded]="autoWorkersExpanded"
                 fill="none"
                 stroke="currentColor"
                 viewBox="0 0 24 24"
                 aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <h2 *ngIf="canEditEntry || !autoWorkersCollapsible"
              class="tt-detail-section__title mb-0">
            Pracovníci na stavbě
          </h2>
          <ul class="diary-workers">
            <li *ngFor="let worker of displayedAutoWorkers; let i = index"
                class="diary-workers__row">
              <span class="tt-detail-value min-w-0">{{ formatAutoWorkerLine(worker) }}</span>
              <app-delete-icon-button
                *ngIf="canEditEntry"
                density="row"
                [ariaLabel]="'Odebrat ' + formatAutoWorkerLine(worker)"
                [disabled]="isSubmitting || isConfirming"
                (onClick)="removeAutoWorker(i)">
              </app-delete-icon-button>
            </li>
          </ul>
          <p *ngIf="!canEditEntry && autoWorkersCollapsible && !autoWorkersExpanded && autoWorkersHiddenCount > 0"
             class="diary-work-summary__more-hint m-0">
            … a dalších {{ autoWorkersHiddenCount }}
          </p>
          <p *ngIf="canEditEntry" class="text-xs text-neutral-500 m-0">
            Ze specifikace stavby. Odebrání platí pro tento den.
          </p>
        </ng-container>
        <ng-template #noAutoWorkers>
          <h2 class="tt-detail-section__title mb-0">Pracovníci na stavbě</h2>
          <p class="text-xs text-neutral-500 m-0">Ze specifikace stavby zatím žádní — zadejte volným textem.</p>
        </ng-template>

        <div class="detail-item">
          <label for="diary-workers-manual" class="tt-detail-label">
            Další pracovníci (volný text)
          </label>
          <textarea id="diary-workers-manual"
                    formControlName="workersManual"
                    rows="3"
                    class="textarea"
                    placeholder="Např. 8 kopáčů, Michal Krechler, firma Pecha…"></textarea>
        </div>
      </section>

      <!-- Mechanizace + subdodavatelé -->
      <section class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Mechanizace a subdodavatelé</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="detail-item">
            <label for="diary-machinery" class="tt-detail-label">Mechanizace</label>
            <textarea id="diary-machinery" formControlName="machinery" rows="3" class="textarea"
                      placeholder="Např. bagr JCB 3CX, kompresor…"></textarea>
          </div>
          <div class="detail-item">
            <label for="diary-subcontractors" class="tt-detail-label">Subdodavatelé</label>
            <textarea id="diary-subcontractors" formControlName="subcontractors" rows="3" class="textarea"
                      placeholder="Např. Výkopy s.r.o. – zemní práce"></textarea>
          </div>
        </div>
      </section>

      <!-- Shrnutí prací — 1 karta = 1 commit akce uživatele (jako user timeline, pro všechny). -->
      <section *ngIf="entry" class="tt-detail-section space-y-4">
        <ng-container *ngIf="workSummaryGroups.length; else noAutoSummary">
          <button *ngIf="workSummaryCollapsible"
                  type="button"
                  class="diary-work-summary__header"
                  [attr.aria-expanded]="workSummaryExpanded"
                  (click)="toggleWorkSummaryExpanded()">
            <div class="min-w-0 flex-1 text-left">
              <h2 class="tt-detail-section__title mb-0">
                Provedené práce ({{ workSummaryGroups.length }})
              </h2>
              <p class="diary-work-summary__toggle-hint m-0 mt-0.5">
                {{ workSummaryExpanded ? 'Skrýt' : ('Zobrazit všech ' + workSummaryGroups.length) }}
              </p>
            </div>
            <svg class="diary-work-summary__chevron"
                 [class.diary-work-summary__chevron--expanded]="workSummaryExpanded"
                 fill="none"
                 stroke="currentColor"
                 viewBox="0 0 24 24"
                 aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <h2 *ngIf="!workSummaryCollapsible"
              class="tt-detail-section__title mb-0">
            Provedené práce
          </h2>

          <div class="diary-timeline space-y-3">
            <div *ngFor="let group of displayedWorkSummaryGroups; trackBy: trackWorkGroup" class="diary-timeline__entry">
              <div class="diary-timeline__node" [attr.data-change-type]="'status_change'"></div>
              <div class="diary-timeline__card">
                <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <strong class="text-sm leading-snug">{{ group.headline }}</strong>
                  <span class="text-xs text-neutral-500">· {{ formatDateTime(group.timestamp) }}</span>
                  <span class="text-xs text-neutral-500 ml-auto">
                    <span class="text-neutral-400">Kdo:</span>&nbsp;<strong>{{ displayPersonName(group.changedByName, group.changedBy) }}</strong>
                  </span>
                </div>

                <div class="diary-work-item__layout"
                     [class.diary-work-item__layout--with-map]="group.showMap">
                  <div class="diary-work-item__content">
                    <div *ngIf="!workGroupIsExcavation(group)" class="diary-timeline__line">
                      <span class="text-neutral-500">Fáze:</span>
                      {{ displayPhase(group.objectType, group.phaseFrom) }} → <strong>{{ displayPhase(group.objectType, group.phaseTo, group.phaseFrom) }}</strong>
                    </div>

                    <ng-container *ngFor="let line of group.displayLines">
                      <div class="diary-timeline__category">
                        <div class="diary-timeline__category-label">
                          <span class="text-neutral-500">{{ line.label }}:</span>
                        </div>
                        <div *ngFor="let item of line.items" class="diary-timeline__subitem">
                          {{ item }}
                        </div>
                      </div>
                    </ng-container>

                    <div *ngIf="group.photos.length" class="diary-work-photos">
                      <span class="text-neutral-500 text-xs">Fotky:</span>
                      <div class="diary-work-photos__grid">
                        <button
                          *ngFor="let photo of group.photos"
                          type="button"
                          class="diary-work-photos__thumb"
                          (click)="openWorkPhotoModal(photo, $event)"
                          [attr.aria-label]="photo.description || 'Zvětšit fotku'"
                        >
                          <img
                            [src]="workPhotoSrc(photo)"
                            [alt]="photo.description || 'Foto'"
                            class="diary-work-photos__img"
                            (error)="onWorkPhotoError($event)"
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div *ngIf="group.showMap && entry" class="diary-work-item__map">
                    <app-diary-work-route-preview [routePoints]="group.routePoints">
                    </app-diary-work-route-preview>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p *ngIf="workSummaryCollapsible && !workSummaryExpanded && workSummaryHiddenGroupCount > 0"
             class="diary-work-summary__more-hint m-0">
            … a dalších {{ workSummaryHiddenGroupCount }} změn
          </p>
        </ng-container>

        <ng-template #noAutoSummary>
          <h2 class="tt-detail-section__title mb-0">Provedené práce</h2>
          <p class="text-xs text-neutral-500 mb-2">Automatický přehled změn bude doplněn po commitu v polygonu.</p>
        </ng-template>

        <label for="diary-work-summary-note" class="tt-detail-label">Doplňující popis prací</label>
        <textarea id="diary-work-summary-note" formControlName="workSummaryNote" rows="3" class="textarea"></textarea>
      </section>

      <!-- Pokyny, odchylky, BOZP, poznámky -->
      <section class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Pokyny, odchylky, BOZP</h2>
        <div class="space-y-3">
          <div class="detail-item">
            <label for="diary-supervisor-instructions" class="tt-detail-label">Pokyny vedoucího stavby</label>
            <textarea id="diary-supervisor-instructions" formControlName="supervisorInstructions" rows="3" class="textarea"></textarea>
          </div>
          <div class="detail-item">
            <span class="tt-detail-label">Odchylky od projektu / neshody</span>
            <p *ngIf="entry?.deviationsAuto; else noAutoDeviations" class="tt-detail-value whitespace-pre-wrap mb-2">
              {{ entry!.deviationsAuto }}
            </p>
            <ng-template #noAutoDeviations>
              <p class="text-xs text-neutral-500 mb-2">Automaticky z poznámek objektů v polygonu za tento den — zatím žádné.</p>
            </ng-template>
            <label for="diary-deviations" class="tt-detail-label">Další odchylky / neshody</label>
            <textarea id="diary-deviations" formControlName="deviations" rows="3" class="textarea"></textarea>
          </div>
          <div class="detail-item">
            <label for="diary-safety-notes" class="tt-detail-label">BOZP / incidenty</label>
            <textarea id="diary-safety-notes" formControlName="safetyNotes" rows="2" class="textarea"></textarea>
          </div>
          <div class="detail-item">
            <label for="diary-notes" class="tt-detail-label">Další poznámky</label>
            <textarea id="diary-notes" formControlName="notes" rows="3" class="textarea"></textarea>
          </div>
        </div>
      </section>

      <section *ngIf="isLocked && entry?.additionalSignatures?.length"
               class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Podpisy — další strany</h2>
        <p class="m-0 mb-3 text-xs text-neutral-600 dark:text-neutral-300">
          Podpis vedoucího stavby je v PDF; níže jsou uložené další podpisy k tomuto dni.
        </p>
        <div *ngFor="let s of entry!.additionalSignatures!; let i = index"
             class="mb-3 tt-surface-muted last:mb-0 space-y-2">
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div class="detail-item">
              <dt class="tt-detail-label">Jméno</dt>
              <dd class="tt-detail-value">{{ s.firstName }}</dd>
            </div>
            <div class="detail-item">
              <dt class="tt-detail-label">Příjmení</dt>
              <dd class="tt-detail-value">{{ s.lastName }}</dd>
            </div>
            <div class="detail-item">
              <dt class="tt-detail-label">Místo</dt>
              <dd class="tt-detail-value">{{ s.place }}</dd>
            </div>
            <div class="detail-item">
              <dt class="tt-detail-label">Pozice</dt>
              <dd class="tt-detail-value">{{ s.role }}</dd>
            </div>
          </dl>
          <p *ngIf="s.note" class="tt-detail-value whitespace-pre-wrap">{{ s.note }}</p>
          <img *ngIf="s.signaturePngBase64" [src]="s.signaturePngBase64" alt="Podpis" class="mt-2 max-h-40 rounded-md bg-white dark:bg-neutral-900" />
        </div>
      </section>

      <section *ngIf="showExtraSignaturesEditor"
               class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Další podpisy (volitelné)</h2>
        <p class="m-0 mb-3 text-xs text-neutral-600 dark:text-neutral-300">
          Stejná pole jako u dokumentů Předání povrchů / Předání odpovědnosti. Uložit zapíše i neúplný řádek; při potvrzení dne jsou povinná jména, místo, pozice a podpis.
        </p>
        <div formArrayName="additionalSignatures" class="space-y-4 mb-4">
          <div *ngFor="let row of additionalSigRows.controls; let i = index"
               [formGroupName]="i"
               class="space-y-3">
            <div class="space-y-2">
              <span class="tt-detail-label">Mustr textu</span>
              <div class="flex flex-wrap gap-x-4 gap-y-2">
                <label *ngFor="let opt of signatureTemplateOptions" class="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio"
                         formControlName="templateType"
                         [value]="opt.value"
                         class="rounded-full border-neutral-400">
                  <span>{{ opt.label }}</span>
                </label>
              </div>
            </div>

            <ng-container *ngIf="isPredaniPovrchuTemplate(row)">
              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">1. Předaný úsek</h4>
                <div class="detail-item">
                  <label [for]="'diary-extra-usek-' + i" class="tt-detail-label">Předaný úsek</label>
                  <textarea [id]="'diary-extra-usek-' + i" formControlName="predanyUsek" rows="3" class="textarea"
                            placeholder="Jaké ulice, odkud kam"></textarea>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">2. Předávající</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-pred-' + i" class="tt-detail-label">Jméno</label>
                    <input [id]="'diary-extra-pred-' + i" formControlName="predavajiciZastupce" class="input" />
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-pred-role-' + i" class="tt-detail-label">Pozice</label>
                    <input [id]="'diary-extra-pred-role-' + i" formControlName="predavajiciRole" class="input" />
                  </div>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">3. Přebírající</h4>
                <p class="m-0 text-xs text-neutral-500">Obec pro protokol: {{ form.get('obec')?.value || '— vyplňte v identifikaci stavby' }}</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-fn-' + i" class="tt-detail-label">Jméno</label>
                    <input [id]="'diary-extra-fn-' + i" formControlName="firstName" class="input" autocomplete="given-name">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-ln-' + i" class="tt-detail-label">Příjmení</label>
                    <input [id]="'diary-extra-ln-' + i" formControlName="lastName" class="input" autocomplete="family-name">
                  </div>
                  <div class="sm:col-span-2">
                    <label [for]="'diary-extra-role-' + i" class="tt-detail-label">Pozice</label>
                    <input [id]="'diary-extra-role-' + i" formControlName="role" class="input" placeholder="např. starosta">
                  </div>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">4. Závady</h4>
                <label class="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" formControlName="defectsFound" class="rounded border-neutral-400">
                  <span>Byly zjištěny závady / nedodělky</span>
                </label>
                <div *ngIf="row.get('defectsFound')?.value" class="space-y-2">
                  <div class="detail-item">
                    <label [for]="'diary-extra-defects-' + i" class="tt-detail-label">Popis závad</label>
                    <textarea [id]="'diary-extra-defects-' + i" formControlName="defectsDescription" rows="3" class="textarea"></textarea>
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-deadline-' + i" class="tt-detail-label">Termín odstranění</label>
                    <input [id]="'diary-extra-deadline-' + i" formControlName="defectsRemediationDeadline" class="input" placeholder="např. 30.6.2026">
                  </div>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">5. Datum, místo a podpisy</h4>
                <p class="m-0 text-xs text-neutral-500">Jedno datum platí jako platnost od i datum protokolu.</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-date-' + i" class="tt-detail-label">Datum</label>
                    <input [id]="'diary-extra-date-' + i" type="date" formControlName="handoverDate" class="input">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-place-' + i" class="tt-detail-label">Místo</label>
                    <input [id]="'diary-extra-place-' + i" formControlName="place" class="input" placeholder="Automaticky z obce">
                  </div>
                </div>
                <div class="detail-item">
                  <label [for]="'diary-extra-note-' + i" class="tt-detail-label">Text protokolu</label>
                  <textarea [id]="'diary-extra-note-' + i" formControlName="note" rows="6" class="textarea"
                            placeholder="Text se doplní z polí výše; lze upravit ručně…"></textarea>
                  <p class="m-0 mt-1 text-xs text-neutral-500">Změna data, úseku nebo závad aktualizuje text, dokud ho ručně neupravíte.</p>
                </div>
                <div class="diary-signature-area">
                  <p class="diary-signature-area__label">Podpis přebírajícího</p>
                  <app-diary-signature-pad #extraSigPad="diarySigPad"
                                         [disabled]="isSubmitting || isConfirming"
                                         (signatureChange)="onExtraSignatureChange(i, $event)"></app-diary-signature-pad>
                </div>
              </section>
            </ng-container>

            <ng-container *ngIf="isPredaniOdpovednostiTemplate(row)">
              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">1. Předávající</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-pred-' + i" class="tt-detail-label">Jméno</label>
                    <input [id]="'diary-extra-pred-' + i" formControlName="predavajiciZastupce" class="input" />
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-pred-role-' + i" class="tt-detail-label">Pozice</label>
                    <input [id]="'diary-extra-pred-role-' + i" formControlName="predavajiciRole" class="input" />
                  </div>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">2. Stavbyvedoucí (přebírající)</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-fn-' + i" class="tt-detail-label">Jméno</label>
                    <input [id]="'diary-extra-fn-' + i" formControlName="firstName" class="input" autocomplete="given-name">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-ln-' + i" class="tt-detail-label">Příjmení</label>
                    <input [id]="'diary-extra-ln-' + i" formControlName="lastName" class="input" autocomplete="family-name">
                  </div>
                  <div class="sm:col-span-2">
                    <label [for]="'diary-extra-role-' + i" class="tt-detail-label">Pozice</label>
                    <input [id]="'diary-extra-role-' + i" formControlName="role" class="input" placeholder="Vedoucí stavby">
                  </div>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">3. Období a důvod</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-from-' + i" class="tt-detail-label">Období od</label>
                    <input [id]="'diary-extra-from-' + i" type="date" formControlName="periodFrom" class="input">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-to-' + i" class="tt-detail-label">Období do</label>
                    <input [id]="'diary-extra-to-' + i" type="date" formControlName="periodTo" class="input">
                  </div>
                </div>
                <div class="detail-item">
                  <label [for]="'diary-extra-reason-' + i" class="tt-detail-label">Důvod / rozsah prací bez dozoru</label>
                  <textarea [id]="'diary-extra-reason-' + i" formControlName="reason" rows="3" class="textarea"
                            placeholder="např. výkopová četa na trase"></textarea>
                </div>
              </section>

              <section class="space-y-3">
                <h4 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100 m-0">4. Datum, místo a podpisy</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-date-' + i" class="tt-detail-label">Datum potvrzení</label>
                    <input [id]="'diary-extra-date-' + i" type="date" formControlName="handoverDate" class="input">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-place-' + i" class="tt-detail-label">Místo</label>
                    <input [id]="'diary-extra-place-' + i" formControlName="place" class="input">
                  </div>
                </div>
                <div class="detail-item">
                  <label [for]="'diary-extra-note-' + i" class="tt-detail-label">Text potvrzení</label>
                  <textarea [id]="'diary-extra-note-' + i" formControlName="note" rows="7" class="textarea"></textarea>
                  <p class="m-0 mt-1 text-xs text-neutral-500">Text se doplní z polí výše, dokud ho ručně neupravíte.</p>
                </div>
                <div class="diary-signature-area">
                  <p class="diary-signature-area__label">Podpis stavbyvedoucího</p>
                  <app-diary-signature-pad #extraSigPad="diarySigPad"
                                         [disabled]="isSubmitting || isConfirming"
                                         (signatureChange)="onExtraSignatureChange(i, $event)"></app-diary-signature-pad>
                </div>
              </section>
            </ng-container>

            <ng-container *ngIf="isJineSignatureTemplate(row)">
              <section class="space-y-3">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div class="detail-item">
                    <label [for]="'diary-extra-fn-' + i" class="tt-detail-label">Jméno</label>
                    <input [id]="'diary-extra-fn-' + i" formControlName="firstName" class="input" autocomplete="given-name">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-ln-' + i" class="tt-detail-label">Příjmení</label>
                    <input [id]="'diary-extra-ln-' + i" formControlName="lastName" class="input" autocomplete="family-name">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-place-' + i" class="tt-detail-label">Místo</label>
                    <input [id]="'diary-extra-place-' + i" formControlName="place" class="input">
                  </div>
                  <div class="detail-item">
                    <label [for]="'diary-extra-role-' + i" class="tt-detail-label">Pozice</label>
                    <input [id]="'diary-extra-role-' + i" formControlName="role" class="input">
                  </div>
                </div>
                <div class="detail-item">
                  <label [for]="'diary-extra-note-' + i" class="tt-detail-label">Vlastní text / prohlášení</label>
                  <textarea [id]="'diary-extra-note-' + i" formControlName="note" rows="6" class="textarea"></textarea>
                </div>
                <div class="diary-signature-area">
                  <p class="diary-signature-area__label">Podpis</p>
                  <app-diary-signature-pad #extraSigPad="diarySigPad"
                                         [disabled]="isSubmitting || isConfirming"
                                         (signatureChange)="onExtraSignatureChange(i, $event)"></app-diary-signature-pad>
                </div>
              </section>
            </ng-container>

            <div class="flex justify-end">
              <app-button
                variant="secondary"
                size="sm"
                (onClick)="removeAdditionalSigRow(i)"
                [disabled]="isSubmitting || isConfirming">
                Odebrat řádek
              </app-button>
            </div>
          </div>
        </div>
        <app-button
          variant="secondary"
          size="sm"
          (onClick)="addAdditionalSigRow()"
          [disabled]="isSubmitting || isConfirming || additionalSigRows.length >= 15">
          Přidat další podpis
        </app-button>
      </section>

      <section *ngIf="showConfirmSection"
               class="tt-detail-section space-y-4">
        <h2 class="tt-detail-section__title">Podpis vedoucího stavby</h2>
        <p class="m-0 mb-2 text-xs text-neutral-600 dark:text-neutral-300">
          Podpis se vloží do PDF spolu s časem a hashem uzamčení verze.
        </p>
        <div class="diary-signature-area">
          <p class="diary-signature-area__label">Podpis vedoucího stavby</p>
          <app-diary-signature-pad #sigPad
                                 [disabled]="isSubmitting || isConfirming"></app-diary-signature-pad>
        </div>
      </section>

      <section *ngIf="entry" class="tt-detail-section space-y-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 class="tt-detail-section__title">Přílohy</h2>
            <p class="m-0 text-xs text-neutral-600 dark:text-neutral-300">
              Fotky, předávací protokoly a dokumenty nahrané k tomuto polygonu v tento den.
            </p>
          </div>
          <div>
            <input
              #diaryAttachmentInput
              type="file"
              class="sr-only"
              multiple
              [disabled]="isUploadingAttachment || isSubmitting || isConfirming"
              (change)="onAttachmentFilesSelected($event)">
            <app-button
              variant="primary"
              size="sm"
              [disabled]="isUploadingAttachment || isSubmitting || isConfirming"
              [loading]="isUploadingAttachment"
              (onClick)="diaryAttachmentInput.click()">
              {{ isUploadingAttachment ? 'Nahrávám…' : 'Nahrát přílohy' }}
            </app-button>
          </div>
        </div>

        <div class="mt-3 flex flex-wrap items-center gap-2 text-xs text-neutral-600 dark:text-neutral-300">
          <span class="inline-flex items-center rounded-full px-2.5 py-1 text-[0.72rem] font-semibold bg-neutral-100 dark:bg-neutral-700/70">
            {{ attachments.length ? (attachments.length + ' příloh') : 'Bez příloh' }}
          </span>
          <span *ngIf="attachments.length">Kliknutím na název soubor stáhnete.</span>
        </div>

        <div *ngIf="attachments.length; else noAttachments" class="mt-3 space-y-2">
          <article *ngFor="let attachment of attachments"
                   class="flex flex-col gap-3 tt-surface-muted sm:flex-row sm:items-center sm:justify-between">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <button type="button"
                        class="max-w-full break-words text-left text-sm font-semibold text-primary-600 dark:text-primary-300 hover:underline"
                        [title]="attachment.originalName"
                        (click)="downloadAttachment(attachment)">
                  {{ attachment.originalName }}
                </button>
                <span *ngIf="attachment.source === 'polygon_document'"
                      class="inline-flex shrink-0 items-center rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-[0.65rem] font-semibold text-neutral-600 dark:text-neutral-300">
                  Dokumenty
                </span>
                <span class="inline-flex shrink-0 items-center rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-[0.65rem] font-semibold text-neutral-600 dark:text-neutral-300">
                  {{ formatBytes(attachment.fileSize) }}
                </span>
              </div>
              <div class="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                <span>{{ attachment.contentType }}</span>
                <span aria-hidden="true">·</span>
                <span>{{ formatDateTime(attachment.uploadedAt) }}</span>
                <span aria-hidden="true">·</span>
                <span>{{ attachment.uploadedBy }}</span>
              </div>
            </div>
            <div class="flex shrink-0 flex-wrap gap-2 sm:justify-end">
              <app-button
                variant="secondary"
                size="sm"
                (onClick)="downloadAttachment(attachment)">
                Stáhnout
              </app-button>
              <app-delete-icon-button
                *ngIf="canDeleteAttachment(attachment)"
                [ariaLabel]="'Smazat přílohu ' + attachment.originalName"
                [title]="'Smazat přílohu ' + attachment.originalName"
                [loading]="deletingAttachmentId === attachment.id"
                [disabled]="isSubmitting || isConfirming"
                (onClick)="deleteAttachment(attachment)"
              ></app-delete-icon-button>
            </div>
          </article>
        </div>

        <ng-template #noAttachments>
          <div class="mt-3 flex items-center gap-3 tt-surface-muted text-sm text-neutral-600 dark:text-neutral-300">
            <div class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300" aria-hidden="true">+</div>
            <div>
              <strong class="block text-neutral-800 dark:text-neutral-100">Žádné přílohy</strong>
              <p class="m-0 text-xs">Nahrajte fotky, dokumenty nebo další podklady k tomuto dni.</p>
            </div>
          </div>
        </ng-template>
      </section>

      <div *ngIf="errorMessage" class="rounded-md bg-error-50 dark:bg-error-900/20 p-3 text-sm text-error-700 dark:text-error-400">
        {{ errorMessage }}
      </div>
    </div>
  </form>

  <div modal-footer class="flex justify-end gap-2 flex-wrap">
    <app-button variant="secondary" (onClick)="onClose()" [disabled]="isSubmitting || isConfirming">
      {{ isLocked ? 'Zavřít' : 'Zavřít bez uložení' }}
    </app-button>
    <app-button *ngIf="canEditForm && !detailLoading"
                variant="secondary"
                (onClick)="onSave()"
                [loading]="isSubmitting"
                [disabled]="isConfirming">
      {{ isLocked ? 'Uložit jako dodatek' : 'Uložit' }}
    </app-button>
    <app-button *ngIf="showConfirmSection && !detailLoading"
                variant="primary"
                (onClick)="onConfirm()"
                [loading]="isConfirming"
                [disabled]="isSubmitting">
      Potvrdit a uzavřít den
    </app-button>
  </div>
</app-modal>

<!-- Náhled fotky nad modalem deníku — stejný vzor jako map.component / popup fotky -->
<app-modal
  [isOpen]="!!selectedWorkPhoto"
  title="Foto"
  [showFooter]="false"
  [closeOnBackdrop]="true"
  width="56rem"
  maxWidth="92vw"
  backdropClass="modal-backdrop--stacked-top"
  (closeEvent)="closeWorkPhotoModal()">
  <div *ngIf="selectedWorkPhoto" class="flex flex-col items-center justify-center gap-3">
    <app-zoomable-photo
      [src]="workPhotoSrc(selectedWorkPhoto)"
      [alt]="selectedWorkPhoto.description || 'Foto'"
    ></app-zoomable-photo>
    <p *ngIf="selectedWorkPhoto.description" class="text-sm text-neutral-700 dark:text-neutral-300 text-center m-0">
      {{ selectedWorkPhoto.description }}
    </p>
  </div>
</app-modal>
